import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';

import { TypedTemplateDirective, provideValueAccessor } from '@core/shared/util';
import { AutocompleteComponent, SelectOption } from '@core/ui';
import { IdentityUser } from '@mp/kernel/users/domain';

@Component({
  selector: 'mpk-user-autocomplete-selector',
  templateUrl: './user-autocomplete-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AutocompleteComponent, TypedTemplateDirective],
  providers: [provideValueAccessor(UserAutocompleteSelectorComponent)],
})
export class UserAutocompleteSelectorComponent implements ControlValueAccessor {
  readonly users: InputSignal<IdentityUser[]> = input<IdentityUser[]>([]);
  readonly hint: InputSignal<string | undefined> = input<string | undefined>();
  readonly selectedValue: ModelSignal<IdentityUser | undefined> = model<IdentityUser | undefined>(undefined);

  readonly emitChanged: OutputEmitterRef<IdentityUser | null> = output<IdentityUser | null>();

  protected readonly searchTerm: WritableSignal<string> = signal<string>('');

  protected readonly filteredOptions: Signal<SelectOption<IdentityUser>[]> = computed<SelectOption<IdentityUser>[]>(
    () => this.getFilteredOptions(),
  );

  onTouched: () => void = () => {};
  onChange: (value: IdentityUser | undefined) => void = () => {};

  private getFilteredOptions(): SelectOption<IdentityUser>[] {
    const filteredUsers: IdentityUser[] = this.filterUsersBySearchTerm(this.searchTerm());
    return this.mapUsersToSelectOptions(filteredUsers);
  }

  private mapUsersToSelectOptions(users: IdentityUser[]): SelectOption<IdentityUser>[] {
    return users.map((user) => ({
      value: user,
      label: `${user.name} (${user.email})`,
    }));
  }

  private filterUsersBySearchTerm(searchTerm: string): IdentityUser[] {
    if (!searchTerm) {
      return this.users();
    }

    const filterValue = searchTerm.toLowerCase();

    return this.users().filter((option: IdentityUser) => {
      const optionString = option.name;

      return optionString.toLowerCase().includes(filterValue);
    });
  }

  protected onSearchTermChange(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
  }

  protected emitValueChange(value: IdentityUser | undefined): void {
    this.emitChanged.emit(value ?? null);
  }

  writeValue(value: IdentityUser | undefined): void {
    this.selectedValue.set(value);
  }

  registerOnChange(onChange: (value: IdentityUser | undefined) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }
}
